import type { Scan, ScanReviewGroup } from "@hobbyshopify/backend-client";
import { gradeLabel as cardGradeLabel, fieldLabel, identityLine, scanTitle } from "../core/primitives/card-display";
import { needsCandidatePick } from "../core/primitives/review-row";
import { ownGradeKey } from "./ebay-links";

/** What ReviewGroupRow shows of a group: its title, identity, notes and own grade. */

/** The row's title: the catalogued card's when the group matched one, else the reading's. */
export const reviewTitle = (group: ScanReviewGroup) => scanTitle(asMatched(group));

/** Year, brand and set, read from the same card as the title. */
export const reviewIdentity = (group: ScanReviewGroup) => identityLine(asMatched(group));

/** Nothing for the price field to start from: the card has no price and the scan no estimate. */
export function hasNoEstimate(group: ScanReviewGroup): boolean {
  return (group.card?.currentPriceCents ?? group.sample.estimatedPriceCents) == null;
}

/** What the reviewer has to settle before the check means anything, one sentence each. */
export function reviewNotes(group: ScanReviewGroup): string[] {
  const notes: string[] = [];
  if (group.bucket === "unvetted") notes.push("A customer's scan created this card. Nobody has checked it yet.");
  for (const d of group.differences) notes.push(`${fieldLabel(d.field)}: ${d.scan || "—"} vs ${d.card || "—"}`);
  if (hasNoEstimate(group) && !needsCandidatePick(group.sample)) {
    notes.push("No comps found. Check eBay, then type a price.");
  }
  return notes;
}

/**
 * The grade the row's eBay links start from: the matched card's, else the
 * reading's, else a grade set by hand or the one the estimate was read from —
 * but not a grade the estimate only borrowed for another one.
 */
export function reviewGradeKey(group: ScanReviewGroup): string | null {
  const scan = group.sample;
  const written = group.card ? cardGradeLabel(group.card) : scan.cardInfo?.grade;
  const named =
    scan.cardInfo?.manual_grade?.trim() || (scan.estimatedPriceFrom === null ? scan.estimatedPriceGrade : null);
  return ownGradeKey(written, named);
}

/** The scan read as the card the group matched, when it matched one. */
const asMatched = (group: ScanReviewGroup): Scan => (group.card ? { ...group.sample, card: group.card } : group.sample);
